import { useEffect, useState } from "react";
import {
  ActionIcon,
  Button,
  Divider,
  Group,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import { Can } from "@/components/Can";
import type { OrderService } from "@/types/api";

interface ServiceFormValues {
  name: string;
  unit: string;
  qty: number | string;
  rate: number | string;
  vat_rate: number | string;
  amount: number | string;
}

function fmtMoney(v: number): string {
  return v.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface ServicesSectionProps {
  orderId: string;
  title: string;
  endpoint: string; // "services-client" | "services-carrier"
  queryKey: readonly unknown[];
}

function ServicesSection({ orderId, title, endpoint, queryKey }: ServicesSectionProps) {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OrderService | null>(null);

  const { data: services, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const r = await api.get<OrderService[]>(`/api/v1/orders/${orderId}/${endpoint}`);
      return Array.isArray(r.data) ? r.data : [];
    },
    staleTime: 0,
  });

  const form = useForm<ServiceFormValues>({
    initialValues: {
      name: "",
      unit: "",
      qty: 1,
      rate: "",
      vat_rate: 0,
      amount: "",
    },
    validate: {
      name: (v) => (String(v).trim() ? null : "Наименование обязательно"),
    },
  });

  // Auto-compute amount = qty * rate
  useEffect(() => {
    const qty = Number(form.values.qty) || 0;
    const rate = Number(form.values.rate) || 0;
    const computed = qty * rate;
    if (form.values.amount !== computed) {
      form.setFieldValue("amount", computed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.qty, form.values.rate]);

  function openCreate() {
    setEditing(null);
    form.reset();
    setModalOpen(true);
  }

  function openEdit(s: OrderService) {
    setEditing(s);
    form.setValues({
      name: s.name,
      unit: s.unit ?? "",
      qty: s.qty,
      rate: s.rate,
      vat_rate: s.vat_rate,
      amount: s.amount,
    });
    setModalOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async (values: ServiceFormValues) => {
      const body = {
        name: values.name,
        unit: values.unit || undefined,
        qty: Number(values.qty) || 0,
        rate: Number(values.rate) || 0,
        vat_rate: Number(values.vat_rate) || 0,
        amount: Number(values.amount) || 0,
      };
      if (editing) {
        await api.patch(`/api/v1/orders/${orderId}/${endpoint}/${editing.id}`, body);
      } else {
        await api.post(`/api/v1/orders/${orderId}/${endpoint}`, body);
      }
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Сохранено", message: "Услуга сохранена" });
      qc.invalidateQueries({ queryKey });
      setModalOpen(false);
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (sid: string) => {
      await api.delete(`/api/v1/orders/${orderId}/${endpoint}/${sid}`);
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Удалено", message: "Услуга удалена" });
      qc.invalidateQueries({ queryKey });
    },
    onError: (err) => {
      notifications.show({ color: "red", title: "Ошибка", message: formatAPIError(err) });
    },
  });

  const rows = services ?? [];
  const total = rows.reduce((acc, s) => acc + (s.amount ?? 0), 0);

  return (
    <>
      <Stack gap="xs">
        <Group justify="space-between" align="center">
          <Text fw={600} size="sm">{title}</Text>
          <Can perm="orders:write">
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>
              Добавить
            </Button>
          </Can>
        </Group>

        {isLoading ? (
          <Text size="sm" c="dimmed">Загрузка…</Text>
        ) : rows.length === 0 ? (
          <Text size="sm" c="dimmed">Услуги не добавлены.</Text>
        ) : (
          <>
            <Table striped withTableBorder withColumnBorders fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>#</Table.Th>
                  <Table.Th>Наименование</Table.Th>
                  <Table.Th>Ед.</Table.Th>
                  <Table.Th>Кол-во</Table.Th>
                  <Table.Th>Тариф</Table.Th>
                  <Table.Th>НДС, %</Table.Th>
                  <Table.Th>Сумма</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((s, idx) => (
                  <Table.Tr key={s.id}>
                    <Table.Td>{idx + 1}</Table.Td>
                    <Table.Td>{s.name}</Table.Td>
                    <Table.Td>{s.unit || "—"}</Table.Td>
                    <Table.Td>{s.qty}</Table.Td>
                    <Table.Td>{fmtMoney(s.rate)}</Table.Td>
                    <Table.Td>{s.vat_rate}</Table.Td>
                    <Table.Td>{fmtMoney(s.amount)}</Table.Td>
                    <Table.Td>
                      <Can perm="orders:write">
                        <Group gap={4} wrap="nowrap">
                          <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(s)}>
                            <IconEdit size={14} />
                          </ActionIcon>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            loading={deleteMutation.isPending}
                            onClick={() => deleteMutation.mutate(s.id)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                      </Can>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="flex-end">
              <Text fw={700} size="sm">
                Итого: {fmtMoney(total)} ₽
              </Text>
            </Group>
          </>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Редактировать услугу" : "Добавить услугу"}
        size="md"
      >
        <Stack gap="sm">
          <TextInput label="Наименование услуги" required {...form.getInputProps("name")} />
          <Group grow>
            <TextInput label="Единица измерения" placeholder="км, ч, шт…" {...form.getInputProps("unit")} />
            <NumberInput
              label="НДС, %"
              placeholder="0"
              min={0}
              max={100}
              {...form.getInputProps("vat_rate")}
            />
          </Group>
          <Group grow>
            <NumberInput
              label="Количество"
              placeholder="1"
              min={0}
              decimalScale={4}
              {...form.getInputProps("qty")}
            />
            <NumberInput
              label="Тариф"
              placeholder="0.00"
              min={0}
              decimalScale={2}
              {...form.getInputProps("rate")}
            />
          </Group>
          <NumberInput
            label="Сумма"
            placeholder="0.00"
            min={0}
            decimalScale={2}
            {...form.getInputProps("amount")}
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={() => setModalOpen(false)}>Отмена</Button>
            <Button
              loading={saveMutation.isPending}
              onClick={() => {
                const result = form.validate();
                if (!result.hasErrors) saveMutation.mutate(form.values);
              }}
            >
              Сохранить
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

// ─── ServicesTab (exported) ──────────────────────────────────────────────────

interface ServicesTabProps {
  orderId?: string;
}

export function ServicesTab({ orderId }: ServicesTabProps) {
  if (!orderId) {
    return (
      <Paper p="xl" withBorder>
        <Text c="dimmed" ta="center" size="sm">
          Сначала сохраните заявку, затем добавьте услуги.
        </Text>
      </Paper>
    );
  }

  return (
    <Paper p="md" withBorder>
      <Stack gap="lg">
        <Title order={5}>Услуги</Title>
        <ServicesSection
          orderId={orderId}
          title="Услуги для заказчика"
          endpoint="services-client"
          queryKey={qk.orderServicesClient(orderId)}
        />
        <Divider />
        <ServicesSection
          orderId={orderId}
          title="Услуги для перевозчика"
          endpoint="services-carrier"
          queryKey={qk.orderServicesCarrier(orderId)}
        />
      </Stack>
    </Paper>
  );
}
